import { confirmedClub } from './club-session';
import { clubStorage } from './club-session';
/**
 * Leagues & Competition State (Microservicio de Ligas, Competición y Simulación)
 * Gestiona selección de ligas, calendario de jornadas, tabla clasificatoria,
 * simulación de partidos (Match Engine) y crónicas/puntuaciones de prensa.
 */
import { Injectable, signal, computed, inject } from '@angular/core';
import {
  LeagueSummary,
  Matchday,
  StandingsRow,
  PlayerEditorialRating,
  LedgerEntry,
  Player,
} from '../models/types';
import {
  LEAGUE_CLUBS,
  generateLeagueMatchdays,
  generateInitialStandings,
} from '../../../data/initialData';
import { INITIAL_LEAGUES } from '../../../data/mockLeagues';
import { extractActiveCompetitionContext } from '../http/competition.api';
import { calculateMatchEngine } from '../simulation/simulation';
import { UiState } from './ui.state';
import { SquadState } from './squad.state';
import { CoachesState } from './coaches.state';

const STORAGE_KEY = 'football_manager_real_v01';

@Injectable({
  providedIn: 'root',
})
export class LeaguesState {
  private readonly ui = inject(UiState);
  private readonly squad = inject(SquadState);
  private readonly coaches = inject(CoachesState);

  readonly leagues = signal<LeagueSummary[]>([]);
  readonly currentLeague = signal<LeagueSummary | null>(null);
  readonly isLeagueLoading = signal<boolean>(false);

  readonly matchdays = signal<Matchday[]>(this.getInitialMatchdays());
  readonly currentMatchdayNumber = signal<number>(this.getInitialMatchdayNumber());
  readonly standings = signal<StandingsRow[]>(this.getInitialStandingsData());
  readonly editorialRatings = signal<PlayerEditorialRating[]>([]);

  readonly currentMatchday = computed<Matchday>(() => {
    const list = this.matchdays() || [];
    const num = this.currentMatchdayNumber();
    const found = list.find((m) => m && m.number === num) || list[0];
    if (found && found.status) {
      return {
        number: found.number ?? (num || 1),
        season: found.season ?? 2026,
        status: found.status ?? 'OPEN',
        date: found.date ?? new Date().toISOString().split('T')[0],
        fixtures: Array.isArray(found.fixtures) ? found.fixtures : [],
      };
    }
    return {
      number: num || 1,
      season: 2026,
      status: 'OPEN',
      date: new Date().toISOString().split('T')[0],
      fixtures: [],
    };
  });

  constructor() {
    // Configure SquadState provider so userClub knows which club belongs to the user
    this.squad.setUserClubIdProvider(() => this.currentLeague()?.userClubId);
  }

  setLeagues(leagues: LeagueSummary[]): void {
    this.leagues.set(leagues);
  }

  setMatchdays(matchdays: Matchday[]): void {
    this.matchdays.set(matchdays);
    this.persist(this.getContextKey('matchdays'), matchdays);
  }

  setStandings(standings: StandingsRow[]): void {
    this.standings.set(standings);
    this.persist(this.getContextKey('standings'), standings);
  }

  private getContextKey(prefix: string): string {
    const contextId = this.currentLeague()?.id || 'global';
    return `${STORAGE_KEY}_${contextId}_${prefix}`;
  }

  syncWithCompetitions(state: any): void {
    if (!state) return;

    const selected = confirmedClub();
    if (selected?.mode === 'PRIVATE') {
      const member = state.privateLeagues?.find((p: any) => p.club.id === selected.clubId && p.privateLeagueId === selected.privateLeagueId);
      if (!member) { this.currentLeague.set(null); this.squad.setActiveUserClubId(null); return; }
      const league: LeagueSummary = { id:member.privateLeagueId, name:member.name, type:'PRIVATE', description:'Liga privada',
        season:member.privateSeasonId || '', totalClubs:0, userClubId:member.club.id, userClubName:member.club.name,
        userClubCode:'', userPosition:0, userPoints:0, currentMatchdayNumber:member.participations?.[0]?.currentReferenceRound || 0,
        totalMatchdays:38, activeUsersCount:0, badgeColor:'#8b5cf6' };
      this.currentLeague.set(league); this.leagues.set([league]);
      this.squad.setActiveUserClubId(member.club.id); this.squad.setUserClubInfo(member.club.name, '', ''); return;
    }
    const ctx = extractActiveCompetitionContext(state);
    if (ctx.status !== 'JOINED' || !ctx.clubId) {
      this.currentLeague.set(null);
      this.squad.setActiveUserClubId(null);
      return;
    }

    const parts: any[] = Array.isArray(state)
      ? state
      : state.participations || state.global?.participations || [];

    const activePart =
      parts.find(
        (p: any) =>
          p?.status?.toUpperCase() === 'ACTIVE' ||
          p?.active === true ||
          p?.isCurrent === true
      ) || parts[0];

    // Global competition details from /api/me/competitions
    const compId = ctx.competitionId || 'comp-global-01';

    const compName =
      state.global?.name ||
      activePart?.competitionName ||
      state.name ||
      'Liga Global de Mánagers';

    const divisionName = ctx.divisionName || '10ª División';

    const seasonName =
      state.global?.seasonName ||
      activePart?.seasonName ||
      'Temporada 2026/27';

    const userClubName = ctx.clubName || 'Mi Club';
    const userClubCode = ctx.clubCode || 'MC';
    const userClubId = ctx.clubId;

    const currentMd =
      activePart?.currentMatchday ||
      state.global?.currentMatchday ||
      1;

    const totalMd =
      activePart?.totalMatchdays ||
      state.global?.totalMatchdays ||
      38;

    const totalClubs =
      activePart?.teamCapacity ||
      activePart?.teamCount ||
      state.global?.teamCapacity ||
      6;

    const globalLeagueSummary: LeagueSummary = {
      id: compId,
      name: compName,
      description: `Competición Oficial · ${divisionName}`,
      type: 'GLOBAL',
      season: seasonName,
      totalClubs: totalClubs,
      userClubId: userClubId,
      userClubName: userClubName,
      userClubCode: userClubCode,
      userPosition: 1,
      userPoints: 0,
      currentMatchdayNumber: currentMd,
      totalMatchdays: totalMd,
      activeUsersCount: totalClubs,
      badgeColor: '#2563eb',
    };

    // PRIVATE has explicit routes and component-local state; this store remains GLOBAL.
    this.leagues.set([globalLeagueSummary]);
    this.currentLeague.set(globalLeagueSummary);
    this.squad.setUserClubDivision(divisionName);

    // CRITICAL: Sincronizar squad con el club y sus datos autoritativos
    this.squad.setActiveUserClubId(userClubId);
    this.squad.setUserClubInfo(userClubName, divisionName, userClubCode);
  }

  returnToLeagueSelection(): void {
    this.currentLeague.set(null);
    if (typeof clubStorage !== 'undefined') {
      clubStorage.removeItem('fm_real_selected_league');
    }
  }

  lockMatchday(): void {
    const mdNumber = this.currentMatchdayNumber();
    this.matchdays.update((prev) => {
      const updated = prev.map((m) =>
        m.number === mdNumber ? { ...m, status: 'LOCKED' as const } : m
      );
      this.persist(this.getContextKey('matchdays'), updated);
      return updated;
    });
    this.ui.showNotification(
      `Jornada ${mdNumber} bloqueada. Alineaciones fijadas.`,
      'info'
    );
  }

  updateEditorialRating(
    playerId: string,
    updates: Partial<PlayerEditorialRating>
  ): void {
    this.editorialRatings.update((prev) =>
      prev.map((r) => {
        if (r.playerId === playerId) {
          const updated = { ...r, ...updates };
          const calculatedPoints =
            updated.baseRating +
            updated.goals * 3 -
            updated.yellowCards * 1 -
            updated.redCards * 3;
          return { ...updated, calculatedPoints };
        }
        return r;
      })
    );
  }

  publishEditorialRatings(): void {
    this.editorialRatings.update((prev) =>
      prev.map((r) => ({ ...r, status: 'PUBLISHED' as const }))
    );
    this.ui.showNotification(
      'Puntuaciones de prensa publicadas y computadas.',
      'success'
    );
  }

  simulateCurrentMatchday(): void {
    const mdNumber = this.currentMatchdayNumber();
    const md = this.currentMatchday();
    const clubList = this.squad.clubs();
    const playerList = this.squad.allPlayers();
    const userC = this.squad.userClub();
    const slots = this.squad.lineupSlots();

    if (md.fixtures.every((f) => f.played)) {
      this.ui.showNotification(
        `La Jornada ${mdNumber} ya ha sido disputada`,
        'info'
      );
      return;
    }

    const ratingsToPublish: PlayerEditorialRating[] = [];

    const updatedFixtures = md.fixtures.map((fixture) => {
      if (fixture.played) return fixture;

      const homeClub =
        clubList.find((c) => c.id === fixture.homeClubId) || clubList[0];
      const awayClub =
        clubList.find((c) => c.id === fixture.awayClubId) || clubList[1];

      const homePlayers =
        homeClub.id === userC.id
          ? (slots
              .map((s) => playerList.find((p) => p.id === s.playerId))
              .filter(Boolean) as Player[])
          : playerList.filter((p) => p.clubId === homeClub.id).slice(0, 11);

      const awayPlayers =
        awayClub.id === userC.id
          ? (slots
              .map((s) => playerList.find((p) => p.id === s.playerId))
              .filter(Boolean) as Player[])
          : playerList.filter((p) => p.clubId === awayClub.id).slice(0, 11);

      const calc = calculateMatchEngine(
        homeClub,
        awayClub,
        homePlayers.length === 11 ? homePlayers : playerList.slice(0, 11),
        awayPlayers.length === 11 ? awayPlayers : playerList.slice(11, 22)
      );

      [...homePlayers, ...awayPlayers].forEach((p) => {
        const goals = calc.goalScorers.filter(
          (g) => g.playerName === p.name
        ).length;
        const baseRating = Math.min(
          5,
          Math.max(
            1,
            +(3.0 + (p.overall - 60) * 0.05 + Math.random() * 0.8).toFixed(1)
          )
        );
        ratingsToPublish.push({
          playerId: p.id,
          playerName: p.name,
          position: p.position,
          minutes: 90,
          baseRating,
          goals,
          yellowCards: Math.random() < 0.15 ? 1 : 0,
          redCards: 0,
          status: 'PUBLISHED',
          revision: 1,
          calculatedPoints: +(baseRating + goals * 3).toFixed(1),
        });
      });

      // Economy updates if user played at home
      if (homeClub.id === userC.id) {
        const totalRevenue = +(
          calc.ticketRevenueM + calc.commercialRevenueM
        ).toFixed(3);
        const newBalance = +(
          userC.economy.availableBalance + totalRevenue
        ).toFixed(3);
        const newLedgerEntry: LedgerEntry = {
          id: `led-match-${Date.now()}`,
          timestamp: new Date().toISOString(),
          type: 'INGRESO',
          amount: totalRevenue,
          concept: `Taquilla y comercial Jornada ${mdNumber} vs ${awayClub.shortName}`,
          referenceId: `MATCHDAY_${mdNumber}`,
          runningBalance: newBalance,
        };

        this.squad.clubs.update((prevClubs) =>
          prevClubs.map((c) =>
            c.id === userC.id
              ? {
                  ...c,
                  economy: {
                    ...c.economy,
                    availableBalance: newBalance,
                    ledger: [newLedgerEntry, ...c.economy.ledger],
                  },
                }
              : c
          )
        );
      }

      return {
        ...fixture,
        homeScore: calc.homeTeamScore,
        awayScore: calc.awayTeamScore,
        played: true,
        engineCalculation: calc,
      };
    });

    // Update matchdays
    this.matchdays.update((prev) => {
      const updated = prev.map((m) =>
        m.number === mdNumber
          ? { ...m, status: 'SCORED' as const, fixtures: updatedFixtures }
          : m
      );
      this.persist(this.getContextKey('matchdays'), updated);
      return updated;
    });

    // Update standings
    this.recalculateStandings(updatedFixtures);
    this.editorialRatings.set(ratingsToPublish);
    this.ui.setActiveTab('matchengine');
    this.ui.showNotification(
      `¡Jornada ${mdNumber} simulada con éxito! Revisa resultados y economía`,
      'success'
    );
  }

  nextMatchday(): void {
    const current = this.currentMatchdayNumber();
    const max = this.matchdays().length;
    if (current < max) {
      this.currentMatchdayNumber.set(current + 1);
      this.persist(this.getContextKey('current_md'), current + 1);
      this.ui.showNotification(`Avanzado a Jornada ${current + 1}`, 'info');
    }
  }

  private recalculateStandings(playedFixtures: any[]) {
    this.standings.update((prevStandings) => {
      const table = [...prevStandings];
      playedFixtures.forEach((f) => {
        if (!f.played) return;
        const h = table.find((s) => s.clubId === f.homeClubId);
        const a = table.find((s) => s.clubId === f.awayClubId);
        if (!h || !a) return;

        h.played += 1;
        a.played += 1;
        h.goalsFor += f.homeScore;
        h.goalsAgainst += f.awayScore;
        a.goalsFor += f.awayScore;
        a.goalsAgainst += f.homeScore;
        h.goalDifference = h.goalsFor - h.goalsAgainst;
        a.goalDifference = a.goalsFor - a.goalsAgainst;

        if (f.homeScore > f.awayScore) {
          h.won += 1;
          h.points += 3;
          h.recentForm = ['W', ...h.recentForm.slice(0, 4)];
          a.lost += 1;
          a.recentForm = ['L', ...a.recentForm.slice(0, 4)];
        } else if (f.homeScore < f.awayScore) {
          a.won += 1;
          a.points += 3;
          a.recentForm = ['W', ...a.recentForm.slice(0, 4)];
          h.lost += 1;
          h.recentForm = ['L', ...h.recentForm.slice(0, 4)];
        } else {
          h.drawn += 1;
          h.points += 1;
          h.recentForm = ['D', ...h.recentForm.slice(0, 4)];
          a.drawn += 1;
          a.points += 1;
          a.recentForm = ['D', ...a.recentForm.slice(0, 4)];
        }
      });

      table.sort((x, y) => {
        if (y.points !== x.points) return y.points - x.points;
        if (y.goalDifference !== x.goalDifference)
          return y.goalDifference - x.goalDifference;
        return y.goalsFor - x.goalsFor;
      });

      this.persist(this.getContextKey('standings'), table);
      return table;
    });
  }

  private persist(key: string, data: any) {
    try {
      clubStorage.setItem(key, JSON.stringify(data));
    } catch {
      // ignore
    }
  }

  private getInitialLeagues(): LeagueSummary[] {
    try {
      const saved = clubStorage.getItem('fm_real_api_leagues');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const hasOldMock = parsed.some(
            (l: any) => l.name?.includes('Santander') || l.name?.includes('1ª División')
          );
          if (!hasOldMock) return parsed;
        }
      }
      return INITIAL_LEAGUES;
    } catch {
      return INITIAL_LEAGUES;
    }
  }

  private getInitialSelectedLeague(): LeagueSummary | null {
    try {
      const saved = clubStorage.getItem('fm_real_selected_league');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (
          parsed?.name &&
          !parsed.name.includes('Santander') &&
          !parsed.name.includes('1ª División')
        ) {
          return parsed;
        }
      }
      return INITIAL_LEAGUES[0] || null;
    } catch {
      return INITIAL_LEAGUES[0] || null;
    }
  }

  private getInitialMatchdays(): Matchday[] {
    return [];
  }

  private getInitialMatchdayNumber(): number {
    try {
      const saved = clubStorage.getItem(`${STORAGE_KEY}_current_md`);
      return saved ? Number(saved) : 1;
    } catch {
      return 1;
    }
  }

  private getInitialStandingsData(): StandingsRow[] {
    return [];
  }
}
